import { defineStore } from "pinia";
import { RouteLocationRaw } from "vue-router";
import { Component, ref } from "vue";
import { v7 } from "uuid";
import MagTilePage from "@/components/tiles/MagTilePage.vue";
import MagTileReport from "@/components/tiles/MagTileReport.vue";
import { packed } from "magnetar-common";

export interface TileState<P extends object> {
    id: string;
    closing: boolean;
    title?: string;
    icon?: string;
    width?: number;
    height?: number;
    maximized?: boolean;
    controls?: {
        maximize: boolean;
        close: boolean;
    };
    client: Component<P>;
    props: P;
}

export const tileDefaults = {
    width: 500,
    height: 500,
};

export type TilePageProps = {
    initialPath: RouteLocationRaw;
};

export interface TileStatePage extends TileState<TilePageProps> {
    client: Component<TilePageProps>;
    props: TilePageProps;
}

export type TileReportProps = {
    user?: packed.PackUserBase;
    reportText: string;
};

export interface TileStateReport extends TileState<TileReportProps> {
    client: Component<TileReportProps>;
    props: TileReportProps;
}

export interface TileControls {
    setTitle(title: string): void;
    setIcon(icon?: string): void;
    setClosing(): void;
    bringToForeground(): void;
}

export const useTileStore = defineStore("tiles", () => {
    const tiles = ref([] as TileState<object>[]);

    const spawnPage = (to: RouteLocationRaw) => {
        tiles.value.push({
            id: v7(),
            closing: false,
            client: MagTilePage,
            props: {
                initialPath: to,
            },
            ...tileDefaults,
        } satisfies TileStatePage);
    };

    const spawnReport = (initialText: string, user?: packed.PackUserBase) => {
        tiles.value.push({
            id: v7(),
            closing: false,
            client: MagTileReport,
            props: {
                user,
                reportText: initialText,
            },
            icon: "warning-circle",
            ...tileDefaults,
        } satisfies TileStateReport);
    };

    const bringToForeground = (id: string) => {
        const idx = tiles.value.findIndex((t) => t.id === id);

        if (idx === -1) {
            return;
        }

        tiles.value.splice(
            tiles.value.length,
            0,
            ...tiles.value.splice(idx, 1),
        );
    };

    const remove = (id: string) => {
        const idx = tiles.value.findIndex((t) => t.id === id);

        if (idx === -1) {
            return;
        }

        tiles.value.splice(idx, 1);
    };

    return {
        tiles,
        remove,
        bringToForeground,
        spawnPage,
        spawnReport,
    };
});
